import type { StorageLocation } from "@storytell/cp-client";
import { getBlock, subscribeToBlockDeltas, subscribeToBlockStop } from "@storytell/streaming";
import { TbAlertCircle } from "solid-icons/tb";
import { Show, createSignal, onCleanup, onMount } from "solid-js";
import { ProcessingSpinner } from "~/components/ui";
import { ImageWithSignedURL } from "../ImageWithSignedURL";

interface ImageGenerationRendererProps {
  blockId: string;
}

interface ImageData {
  type: "image";
  storageLocation?: StorageLocation;
  metadata?: {
    model?: string;
    revisedPrompt?: string;
    prompt?: string;
    size?: string;
    quality?: string;
    style?: string;
  };
}

export function ImageGenerationRenderer(props: ImageGenerationRendererProps) {
  const block = getBlock(props.blockId);

  const [imageData, setImageData] = createSignal<ImageData | null>(null);
  const [isStreaming, setIsStreaming] = createSignal(true);

  onMount(() => {
    const unsubscribeDelta = subscribeToBlockDeltas(props.blockId, (delta: string) => {
      try {
        const parsed = JSON.parse(delta) as ImageData;
        if (parsed.type === "image") {
          setImageData(parsed);
        }
      } catch (error) {
        console.warn("Failed to parse image data:", error);
      }
    });

    const unsubscribeStop = subscribeToBlockStop(props.blockId, () => {
      setIsStreaming(false);
    });

    onCleanup(() => {
      unsubscribeDelta();
      unsubscribeStop();
    });
  });

  const storageLocation = () => imageData()?.storageLocation;
  const metadata = () => imageData()?.metadata;

  return (
    <div
      data-renderer="image-generation-renderer"
      class="text-base bg-panel pl-5 pr-5 -ml-2 pt-3 pb-3 rounded-xl border border-primary-border"
    >
      <Show
        when={storageLocation()}
        fallback={
          <Show when={isStreaming()}>
            <div class="flex items-center gap-3 text-sm text-secondary-text">
              <ProcessingSpinner class="w-4 h-4" />
              <span>Generating image...</span>
            </div>
          </Show>
        }
      >
        <div class="space-y-3">
          <ImageWithSignedURL storageLocation={storageLocation()!} metadata={metadata()} />

          <div class="flex items-start gap-2 p-3 bg-inset rounded-lg text-xs text-secondary-text">
            <TbAlertCircle class="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Image generation is a beta feature and is more prone to errors like misspellings in charts and graphs.
              Make sure to double check the underlying data before using.
            </span>
          </div>

          <Show when={metadata() && (metadata()?.model || metadata()?.revisedPrompt)}>
            <div class="space-y-2 text-xs text-secondary-text">
              <Show when={metadata()?.revisedPrompt}>
                <div class="space-y-1">
                  <div class="font-medium text-tertiary-text">Revised prompt:</div>
                  <div class="text-secondary-text italic">{metadata()?.revisedPrompt}</div>
                </div>
              </Show>

              <Show when={metadata()?.size}>
                <div class="flex items-center gap-2">
                  <span class="font-medium text-tertiary-text">Size:</span>
                  <span>{metadata()?.size}</span>
                </div>
              </Show>

              <Show when={metadata()?.quality}>
                <div class="flex items-center gap-2">
                  <span class="font-medium text-tertiary-text">Quality:</span>
                  <span>{metadata()?.quality}</span>
                </div>
              </Show>

              <Show when={metadata()?.style}>
                <div class="flex items-center gap-2">
                  <span class="font-medium text-tertiary-text">Style:</span>
                  <span>{metadata()?.style}</span>
                </div>
              </Show>
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
}
